import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Button,
  TextField,
  Rating,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import { Z_INDEX } from '../../constants/zIndex';
import { getInitials } from '../editor/features/roles/utils/rolColorUtils';
import { prepareRoleColorForChat } from '../../utils/roleColorValidation';

/**
 * AgentReviewDialog — Star rating + optional comment dialog for agent reviews.
 * Pre-fills with existing review if present.
 */
const AgentReviewDialog = ({ open, onClose, role, existingReview, onSubmit, onDelete }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setRating(existingReview?.rating || 0);
      setComment(existingReview?.comment || '');
    }
  }, [open, existingReview]);

  if (!role) return null;

  const bgColor = role.color || theme.palette.primary.main;
  const roleColors = prepareRoleColorForChat(bgColor, theme);

  const handleSubmit = async () => {
    if (rating === 0) return;
    setIsSubmitting(true);
    try {
      await onSubmit({ rating, comment: comment.trim() || null });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      await onDelete();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
      sx={{ zIndex: Z_INDEX.MODALS }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 6 }}>
        <Avatar
          sx={{
            width: 40,
            height: 40,
            fontSize: '1rem',
            fontWeight: 600,
            background: roleColors.avatarBgGradient,
            boxShadow: roleColors.avatarShadow,
            color: roleColors.avatarText,
          }}
        >
          {getInitials(role.name)}
        </Avatar>
        <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
          {role.name}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label={t('common.close', 'Close')}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('chat.agentMenu.leaveReview', 'Leave a review')}
          </Typography>
          <Rating
            value={rating}
            onChange={(_, value) => setRating(value || 0)}
            size="large"
          />
          <TextField
            multiline
            rows={3}
            fullWidth
            size="small"
            placeholder={t('review.commentPlaceholder', 'What did you think? (optional)')}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, justifyContent: existingReview ? 'space-between' : 'flex-end' }}>
        {existingReview && (
          <Button
            onClick={handleDelete}
            disabled={isSubmitting}
            color="error"
            startIcon={<DeleteOutlineIcon />}
            size="small"
          >
            {t('review.delete', 'Delete review')}
          </Button>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={isSubmitting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting || rating === 0}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
          >
            {t('common.submit', 'Submit')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AgentReviewDialog;
